import React from "react";
import { WarningOctagon, Plus, Check } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

// v0.6 — Compliance-risk banner (client-edit declutter item 1). Reads the
// structured `compliance_findings` (migrated out of free-text notes) and
// surfaces them as a red, actionable warning. Acknowledge + Add-registration
// are PER-COUNTRY (decision #1: acknowledging one country must NOT mark the
// others handled). Renders nothing when there are no UNACKNOWLEDGED findings.
export const ComplianceRiskBanner = ({ findings, onAcknowledge, onAddRegistration }) => {
  const { t } = useTranslation();
  const open = (findings || []).filter((x) => !x.acknowledged);
  if (open.length === 0) return null;
  return (
    <div
      data-testid="compliance-risk-banner"
      className="border border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/50 rounded-lg p-4 space-y-3"
    >
      <div className="flex items-start gap-3">
        <WarningOctagon size={22} weight="fill" className="text-rose-600 dark:text-rose-400 mt-0.5 shrink-0" />
        <div className="space-y-3 flex-1">
          <div className="space-y-1.5">
            <h3 className="text-sm font-semibold text-rose-900 dark:text-rose-100" data-testid="compliance-banner-title">
              {t("ui.compliance_risk.title", { defaultValue: "Possible under-registration" })}
            </h3>
            <p className="text-[13px] text-rose-800 dark:text-rose-200 leading-relaxed">
              {t("ui.compliance_risk.body_before", { defaultValue: "This client has genuine local obligations with no matching registration on file. Over-threshold distance sales are being routed to local filing for want of an OSS registration. Resolve each country below —" })} <span className="font-semibold">{t("ui.compliance_risk.body_link", { defaultValue: "add a local registration / register for OSS" })}</span>{t("ui.compliance_risk.body_after", { defaultValue: ", or acknowledge it if it will be handled separately." })}
            </p>
          </div>
          <ul className="space-y-2" data-testid="compliance-banner-chips">
            {open.map((x) => (
              <li
                key={x.country_code}
                data-testid={`compliance-finding-${x.country_code}`}
                className="flex flex-wrap items-center gap-2 bg-white dark:bg-rose-900/40 border border-rose-200 dark:border-rose-800 rounded-md px-3 py-2"
              >
                <span
                  data-testid={`compliance-chip-${x.country_code}`}
                  className="inline-flex items-center gap-1 text-[12px] tabular-nums text-rose-800 dark:text-rose-100"
                >
                  <span className="font-semibold">{x.country_code}</span>
                  {x.article && <span className="text-rose-500 dark:text-rose-300">· {x.article}</span>}
                </span>
                {x.note && (
                  <span className="text-[11px] text-rose-600 dark:text-rose-300 italic">{x.note}</span>
                )}
                <div className="flex gap-2 ml-auto">
                  <button
                    type="button"
                    data-testid={`compliance-add-registration-${x.country_code}`}
                    onClick={() => onAddRegistration(x.country_code)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-[10px] tabular-nums font-semibold"
                  >
                    <Plus size={11} weight="bold" /> {t("ui.compliance_risk.add_registration", { defaultValue: "Add registration" })}
                  </button>
                  <button
                    type="button"
                    data-testid={`compliance-acknowledge-${x.country_code}`}
                    onClick={() => onAcknowledge(x.country_code)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-rose-300 dark:border-rose-700 bg-white dark:bg-transparent text-rose-800 dark:text-rose-200 text-[10px] tabular-nums hover:bg-rose-100 dark:hover:bg-rose-900/40"
                  >
                    <Check size={11} weight="bold" /> {t("ui.compliance_risk.acknowledge", { defaultValue: "Acknowledge" })}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
